import React, { useEffect } from 'react';
import {
    Users,
    Wallet,
    Target,
    LayoutDashboard,
    Bot,
    Clock,
    ClipboardList,
    MoveRight
} from 'lucide-react';

const QUESTIONNAIRE_URL = 'https://docs.google.com/document/d/1Th11lWn6FSxHeRGATPAjyYbSDpGc5QJUgDkYB68o1Ag/edit';

const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

const NumRow = ({ n, h, s }) => (
    <div className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
        <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{n}</div>
        <div>
            <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{h}</h3>
            <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{s}</p>
        </div>
    </div>
);

export default function SanchitStart() {

    useEffect(() => {
        document.title = "Sanchit Kedia | Getting Started";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Sanchit Kedia</span>
                </div>
                <div className="bg-emerald-500/20 backdrop-blur-lg border border-emerald-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-emerald-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Getting Started</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Built for you after our call · 12 Sep 2026
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        Trust Fund Baby,<br />meet your distribution.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-6 font-light">
                        Hey Sanchit. This page is your start line: what we locked on the call, the five minutes we need from you, and exactly what happens after.<br className="hidden sm:block" /> No forms, no fluff, voice notes welcome.
                    </p>
                </section>

                {/* LOCKED ON THE CALL */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Already locked on the call</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            We listened.<br />Here is what stands.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Users} heading="The audience" subheading="parents of kids under 10 and newborns · we go wide with FOMO-driven content, because a Rs 1,000 SIP is a trust decision, not a sales pitch" accent="indigo" />
                        <InfoCard icon={Wallet} heading="Your economics respected" subheading="trail-commission model means your customer acquisition cost must stay low · owned distribution is the only channel that gets cheaper every month" accent="indigo" />
                        <InfoCard icon={Bot} heading="Your clone records" subheading="built on our own Indian-skin-tone LoRA · you stay the brain, the camera stops being your bottleneck" accent="indigo" />
                        <InfoCard icon={LayoutDashboard} heading="Setter and dashboard included" subheading="skip the ManyChat build · our Meta-verified AI setter answers comments, delivers resources and nurtures DMs, with AI credits on us" accent="indigo" />
                        <InfoCard icon={Target} heading="The metric that matters to you" subheading="reach plus comments and DM conversations · the proxy for SIPs, tracked on every single video in your dashboard" accent="indigo" />
                        <InfoCard icon={Clock} heading="The timing logic" subheading="month one is the slowest, month three compounds · start now and the audience lands exactly when BSE clears and the app goes live" accent="indigo" />
                    </div>
                </section>

                {/* THE 5 MINUTES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">Your five minutes</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Six questions.<br />That is all we need.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Everything else came from the call. These six close the gaps between here and your exact number.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4 mb-10">
                        <NumRow n="01" h="Positioning" s="Pure solo AI founder behind Trust Fund Baby, or the operator who already built hotels and construction, now building wealth for kids? One line." />
                        <NumRow n="02" h="Who your parents already follow" s="3 to 5 Instagram accounts your target parents watch today. This is what we run the market research on." />
                        <NumRow n="03" h="Your story in short" s="The 2 or 3 real moments that made you build Trust Fund Baby. A voice note is perfect." />
                        <NumRow n="04" h="Camera boundaries" s="Are your own kids or family ever on camera, or never? Anything else off-limits?" />
                        <NumRow n="05" h="The money math, confirmed" s="Rs 1,000 per month SIP is the entry. Anything else a customer can buy? And what does a win customer look like at month 12?" />
                        <NumRow n="06" h="Clone training material" s="Any existing solo footage or audio of you? If not, we plan one short recording session. Either way works." />
                    </div>

                    <div className="text-center">
                        <a
                            href={QUESTIONNAIRE_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                        >
                            <ClipboardList className="w-5 h-5" /> Open your questionnaire <MoveRight className="w-5 h-5" />
                        </a>
                        <p className="text-xs sm:text-sm text-white/40 mt-4">Prefer voice notes? Answer on our thread. Same effect.</p>
                    </div>
                </section>

                {/* WHAT HAPPENS NEXT */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">What happens next</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            From this page<br />to posting daily.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="You send the six answers" s="Five minutes, today. Voice notes count." />
                        <NumRow n="02" h="We finish your research the same day" s="Your market, the accounts your parents follow, and what the winning content in your niche actually looks like." />
                        <NumRow n="03" h="Tomorrow's call: KPI and the number, in writing" s="The exact monthly investment and the exact guarantee we sign against. No ranges, no surprises." />
                        <NumRow n="04" h="Sign, then a 7-day onboarding" s="Clone trained on your voice and face, positioning locked, first scripts approved by you." />
                        <NumRow n="05" h="The engine goes live" s="Daily content, setter working your comments and DMs, every video tracked. First month is the worst month. Third month is the one you screenshot." />
                    </div>

                    <div className="max-w-3xl mx-auto mt-10">
                        <div className="rounded-2xl border border-amber-500/20 bg-amber-900/10 p-6 sm:p-8 text-center">
                            <div className="text-amber-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">The guarantee</div>
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                A KPI written into a legally bound contract, set after your research. If we miss it, you get 100% of your money back. Zero followers today is not a problem, it is the case study.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        Five minutes now.<br />Compounding by December.
                    </h2>
                    <a
                        href={QUESTIONNAIRE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        <ClipboardList className="w-5 h-5" /> Start the six questions <MoveRight className="w-5 h-5" />
                    </a>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
